import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_METRIC_VIEW_TYPE,
  CHART_SHARE_CALCULATION_TYPE,
} from '@/entities/chart/model/constants';
import useChartTableMeta from '@/features/(charts)/chart-detail/hooks/use-chart-table-meta';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import { CrossTableStrategyFactory } from '@/features/(charts)/chart-renderer/lib/Cross/lib/strategies';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';

import { useOpenChartClickDialog } from '../../hooks/use-open-chart-click-dialog';
import type { ChartConfig, ColumnBuilderParams, DataBuilderParams } from './types';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  enabled?: boolean;
};

const useChartCrossTableData = (props: Props) => {
  const { chart, enabled = true } = props;
  const openChartClickDialog = useOpenChartClickDialog();
  const table = props.tableData as ChartCrossTableRes;
  const getLabel = useChartLabel(table.field_item_meta);
  const { baseColumns, getLevelRowMap, getTotalRowMap } =
    useChartTableMeta(props);

  // Create configuration object
  const configs: ChartConfig[] = useMemo(() => {
    return chart.series.map((series) => {
      const viewType = series.options?.view_type;
      const shareCalculationType =
        series.options?.share_calculation_type ||
        CHART_SHARE_CALCULATION_TYPE.COLUMN;

      const isPercentage = viewType === CHART_METRIC_VIEW_TYPE.PERCENTAGE;

      // Total 표시 정책 (PRD 기반)
      const { showRowTotal, showColumnTotal } = getTotalDisplayConfig(
        viewType,
        shareCalculationType,
        series.options?.show_row_total,
        series.measure.calculation,
      );

      const showGrandTotal =
        viewType !== undefined &&
        shareCalculationType === CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL;

      return {
        isPercentage,
        isCompare: chart.target.is_compare || false,
        isCellSentimentCalculation:
          isPercentage &&
          shareCalculationType === CHART_SHARE_CALCULATION_TYPE.CELL,
        shareCalculationType,
        sentimentSegment:
          (series.options
            ?.share_cell_calculation_segment as unknown as FeedbackSentiment) ||
          FeedbackSentiment.NEGATIVE,
        showGrandTotal,
        showRowTotal,
        showColumnTotal,
      };
    });
  }, [chart.series, chart.target.is_compare]);

  // 첫 번째 시리즈의 cross_field candidates (기존 호환성)
  const yHeaders = useMemo(() => {
    if (chart.series[0].cross_field?.type === CHART_FIELD.FEEDBACK_ALL)
      return [CHART_ALL_FEEDBACK_ALIAS];
    return chart.series[0]?.cross_field?.candidates || [];
  }, [chart.series]);

  // sortedCandidates: x축 후보 정렬 (기존 로직 유지)
  const sortedCandidates = useMemo(() => {
    const tableData = table.tables[0] || [];
    const candidatesSet = new Set(tableData.map((d) => d.x));
    const candidates = Array.from(candidatesSet);

    return candidates;
  }, [table.tables]);

  // statistics 계산 (전체 시리즈의 최대/최소값)
  const statistics = useMemo(() => {
    let max = -Infinity;
    let min = Infinity;

    table.tables.forEach((tableData) => {
      tableData.forEach((row) => {
        row.values.forEach((cell) => {
          if (cell.value > max) max = cell.value;
          if (cell.value < min) min = cell.value;
        });
      });
    });

    if (table.compare_tables) {
      table.compare_tables.forEach((tableData) => {
        tableData.forEach((row) => {
          row.values.forEach((cell) => {
            if (cell.value > max) max = cell.value;
            if (cell.value < min) min = cell.value;
          });
        });
      });
    }

    return { max, min };
  }, [table.tables, table.compare_tables]);

  // onCellClick handler
  const onCellClick = useCallback(
    (
      x: StringNumberValue | null,
      y: StringNumberValue | null,
      z: StringNumberValue | null,
      comparedTarget: boolean,
      seriesIndex: number,
    ) => {
      openChartClickDialog({
        type: DETAIL_MODAL_TYPE.CHART,
        alias: JSON.stringify({
          title: chart.name,
          chart: {
            ...chart,
            target: {
              ...chart.target,
              is_compare: comparedTarget,
            },
          },
          seriesIndex,
          dashboardCardInfo: props.dashboardCardInfo,
        }),
        options: {
          isAll: false,
          chart: {
            ...chart,
            target: {
              ...chart.target,
              is_compare: comparedTarget,
            },
          },
          cell: {
            compared_target: false,
            item_x: x || '',
            item_x_name: getLabel(chart.field.type, x || ''),
            item_y: y ?? undefined,
            item_y_name: y
              ? getLabel(chart.series[seriesIndex]?.cross_field?.type!, y)
              : undefined,
            item_z: z || undefined,
            item_z_name: z
              ? sentimentMap[z as FeedbackSentiment].value
              : undefined,
            series_index: seriesIndex,
          },
          dashboardCardInfo: props.dashboardCardInfo,
        },
      });
    },
    [chart, getLabel, openChartClickDialog, props.dashboardCardInfo],
  );

  // Determine mode and create strategy
  const mode = useMemo(
    () => CrossTableStrategyFactory.determineMode(chart),
    [chart],
  );

  // Prepare column builder parameters
  const columnParams: ColumnBuilderParams = useMemo(
    () => ({
      chart,
      configs,
      baseColumns,
      yHeaders,
      sortedCandidates,
      statistics,
      helpers: {
        getLabel,
        getLevelRowMap,
        getTotalRowMap,
        onCellClick,
      },
    }),
    [
      chart,
      configs,
      baseColumns,
      yHeaders,
      sortedCandidates,
      statistics,
      getLabel,
      getLevelRowMap,
      getTotalRowMap,
      onCellClick,
    ],
  );

  // Prepare data builder parameters
  const dataParams: DataBuilderParams = useMemo(
    () => ({
      chart,
      configs,
      crossTableData: table,
      sortedCandidates,
      yHeaders,
      helpers: {
        getLevelRowMap,
        getTotalRowMap,
      },
    }),
    [
      chart,
      configs,
      table,
      sortedCandidates,
      yHeaders,
      getLevelRowMap,
      getTotalRowMap,
    ],
  );

  // Create strategy instance
  const strategy = useMemo(
    () =>
      CrossTableStrategyFactory.createStrategy(mode, columnParams, dataParams),
    [mode, columnParams, dataParams],
  );

  // Build columns using strategy
  const columns: ColumnsType<any> = useMemo(
    () => (enabled ? strategy.buildColumns() : []),
    [strategy, enabled],
  );

  // Build dataSource using strategy
  const dataSource = useMemo(
    () => (enabled ? strategy.buildDataSource() : []),
    [strategy, enabled],
  );

  return {
    columns,
    dataSource,
    yHeaders,
  };
};

/**
 * Total 표시 정책을 정의한 맵
 * [showRowTotal, showColumnTotal]
 */
const TOTAL_DISPLAY_POLICY: Record<
  CHART_SHARE_CALCULATION_TYPE,
  [boolean, boolean]
> = {
  [CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL]: [true, true],
  [CHART_SHARE_CALCULATION_TYPE.ROW]: [true, false],
  [CHART_SHARE_CALCULATION_TYPE.COLUMN]: [false, true],
  [CHART_SHARE_CALCULATION_TYPE.CELL]: [false, false],
};

/**
 * view_type과 share_calculation_type에 따라 Total 표시 여부를 결정
 */
const getTotalDisplayConfig = (
  viewType: CHART_METRIC_VIEW_TYPE | undefined,
  shareCalculationType: CHART_SHARE_CALCULATION_TYPE,
  showRowTotalOption: boolean | undefined,
  calculation: CHART_CALCULATION,
): { showRowTotal: boolean; showColumnTotal: boolean } => {
  if (calculation === CHART_CALCULATION.AVERAGE) {
    return {
      showRowTotal: false,
      showColumnTotal: false,
    };
  }

  // COUNT 모드: show_row_total 옵션 사용, Column Total은 항상 표시
  if (viewType === CHART_METRIC_VIEW_TYPE.COUNT) {
    return {
      showRowTotal: showRowTotalOption ?? false,
      showColumnTotal: true,
    };
  }

  // PERCENTAGE 모드: share_calculation_type에 따라 결정
  if (viewType === CHART_METRIC_VIEW_TYPE.PERCENTAGE) {
    const [showRowTotal, showColumnTotal] = TOTAL_DISPLAY_POLICY[
      shareCalculationType
    ] ?? [false, false];
    return { showRowTotal, showColumnTotal };
  }

  // 기본값: show_row_total 옵션 사용
  return {
    showRowTotal: showRowTotalOption ?? false,
    showColumnTotal: false,
  };
};

export default useChartCrossTableData;
